/**
 * 来場者アンケートサービス
 * Google Form連携アンケートの公開情報取得とpt受取を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';
import { invalidateVisitorHomeBootstrapCache } from './visitorHomeBootstrapCache';

/** アンケートポイント付与RPC名 */
const SURVEY_CLAIM_POINTS_RPC_NAME = 'survey_claim_points';

/** アンケート取得失敗エラーコード */
export const VISITOR_SURVEY_FETCH_FAILED_CODE = 'VISITOR_SURVEY_FETCH_FAILED';

/** アンケートポイント付与失敗エラーコード */
export const VISITOR_SURVEY_CLAIM_FAILED_CODE = 'VISITOR_SURVEY_CLAIM_FAILED';

/** アンケート未検出（コード不一致）エラーコード */
export const VISITOR_SURVEY_CLAIM_NOT_FOUND_CODE = 'VISITOR_SURVEY_CLAIM_NOT_FOUND';

/** アンケート済み取得エラーコード */
export const VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_CODE = 'VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED';

/** アンケートポイント付与セッション不正エラーコード */
export const VISITOR_SURVEY_CLAIM_SESSION_INVALID_CODE = 'VISITOR_SURVEY_CLAIM_SESSION_INVALID';

/** アンケートコードの試行回数上限（レート制限超過）エラーコード */
export const VISITOR_SURVEY_CLAIM_RATE_LIMITED_CODE = 'VISITOR_SURVEY_CLAIM_RATE_LIMITED';

/**
 * RPCエラーに特定メッセージが含まれるか判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @param {string} keyword - 判定キーワード
 * @returns {boolean} 含まれる場合true
 */
const includesRpcErrorMessage = (rpcError, keyword) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes(keyword);
};

/**
 * 公開アンケート一覧を取得する
 * @returns {Promise<Array<{
 * surveyId: string,
 * title: string,
 * description: string,
 * formUrl: string,
 * rewardPoints: number,
 * publishedAt: string | null,
 * }>>} アンケート一覧
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorGetPublishedSurveys = async () => {
  const supabaseClient = getSupabaseClient();

  const surveysResult = await supabaseClient
    .from('surveys')
    // reward_codeは公開一覧へ出さない（運営がGoogle Formの回答完了画面へ設定し、来場者は手入力で受け取る）
    .select('id, title, description, form_url, reward_points, published_at')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(30);

  if (surveysResult.error) {
    logError('surveys 取得に失敗しました。', {
      code: surveysResult.error.code,
      message: surveysResult.error.message,
      details: surveysResult.error.details,
    });

    throw new AppError('アンケート一覧の取得に失敗しました。', {
      code: VISITOR_SURVEY_FETCH_FAILED_CODE,
      statusCode: 500,
      details: {
        message: surveysResult.error.message,
      },
    });
  }

  if (!Array.isArray(surveysResult.data)) {
    throw new AppError('surveys のレスポンス形式が不正です。', {
      code: VISITOR_SURVEY_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { data: surveysResult.data },
    });
  }

  return surveysResult.data.map((surveyRow) => {
    const surveyId = surveyRow?.id;
    const title = surveyRow?.title;
    const description = surveyRow?.description ?? '';
    const formUrl = surveyRow?.form_url;
    const rewardPoints = Number(surveyRow?.reward_points);
    const publishedAt = surveyRow?.published_at ?? null;

    if (
      !surveyId
      || !title
      || !formUrl
      || Number.isNaN(rewardPoints)
      || rewardPoints <= 0
    ) {
      throw new AppError('surveys のレスポンス形式が不正です。', {
        code: VISITOR_SURVEY_FETCH_FAILED_CODE,
        statusCode: 500,
        details: { surveyRow },
      });
    }

    return {
      surveyId,
      title,
      description,
      formUrl,
      rewardPoints,
      publishedAt,
    };
  });
};

/**
 * アンケートポイント付与RPCエラーコードを変換する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {string} 変換済みエラーコード
 */
const mapSurveyClaimRpcErrorCode = (rpcError) => {
  if (
    includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN')
    || includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_SURVEY_CLAIM_SESSION_INVALID_CODE;
  }

  if (
    includesRpcErrorMessage(rpcError, 'SURVEY_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'SURVEY_NOT_PUBLISHED')
    || includesRpcErrorMessage(rpcError, 'INVALID_REWARD_CODE')
  ) {
    return VISITOR_SURVEY_CLAIM_NOT_FOUND_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'SURVEY_ALREADY_CLAIMED')) {
    return VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'RATE_LIMIT_EXCEEDED')) {
    return VISITOR_SURVEY_CLAIM_RATE_LIMITED_CODE;
  }

  return VISITOR_SURVEY_CLAIM_FAILED_CODE;
};

/**
 * アンケート回答コードでポイントを取得する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} rewardCode - アンケート報酬コード
 * @returns {Promise<{
 * claimed: boolean,
 * visitorId: string,
 * surveyId: string,
 * pointsGranted: number,
 * currentPoint: number,
 * pointLogId: string,
 * }>} ポイント取得結果
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorSurveyClaimPoints = async (sessionToken, rewardCode) => {
  /** セッショントークン検証結果 */
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_SURVEY_CLAIM_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** 報酬コード検証結果 */
  const rewardCodeValidation = validateRequiredString(rewardCode, 'アンケートコード');

  if (!rewardCodeValidation.isValid) {
    throw new AppError(rewardCodeValidation.message, {
      code: VISITOR_SURVEY_CLAIM_FAILED_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(SURVEY_CLAIM_POINTS_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_reward_code: rewardCode.trim(),
  });

  if (error) {
    /** 変換済みエラーコード */
    const mappedErrorCode = mapSurveyClaimRpcErrorCode(error);

    logError('survey_claim_points RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('アンケートポイント取得に失敗しました。', {
      code: mappedErrorCode,
      statusCode: mappedErrorCode === VISITOR_SURVEY_CLAIM_FAILED_CODE ? 500 : 400,
      details: {
        message: error.message,
      },
    });
  }

  // コード違いの時、DBは試行回数をcommitさせるため例外ではなく0行を返す（旧DBは例外で返す）。
  if (Array.isArray(data) && data.length === 0) {
    throw new AppError('アンケートコードが見つかりません。', {
      code: VISITOR_SURVEY_CLAIM_NOT_FOUND_CODE,
      statusCode: 400,
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = Array.isArray(data) ? data[0] ?? null : data ?? null;

  /** claimed */
  const claimed = Boolean(rpcRow?.claimed);

  /** visitorId */
  const visitorId = rpcRow?.visitor_id;

  /** surveyId */
  const surveyId = rpcRow?.survey_id;

  /** pointsGranted */
  const pointsGranted = Number(rpcRow?.points_granted);

  /** currentPoint */
  const currentPoint = Number(rpcRow?.current_point);

  /** pointLogId */
  const pointLogId = rpcRow?.point_log_id;

  if (
    !claimed
    || !visitorId
    || !surveyId
    || Number.isNaN(pointsGranted)
    || Number.isNaN(currentPoint)
    || !pointLogId
  ) {
    throw new AppError('survey_claim_points のレスポンス形式が不正です。', {
      code: VISITOR_SURVEY_CLAIM_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  // pt残高が変わるため、ホーム集約データのTTLキャッシュを破棄する
  invalidateVisitorHomeBootstrapCache(sessionToken.trim());

  return {
    claimed,
    visitorId,
    surveyId,
    pointsGranted,
    currentPoint,
    pointLogId,
  };
};
